import type { Board, BoardDetails, BoardSuggestion, DiscoveryProduct, FindsCard, ImagePosition, Reaction, TriageProduct, UserProfile } from "./types";

async function postJson<T>(path: string, body: unknown): Promise<{ ok: true; data: T } | { ok: false; error: string }> {
  const response = await fetch(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    return { ok: false, error: (data as { error?: string }).error || "Request failed" };
  }

  return { ok: true, data: data as T };
}

async function patchJson<T>(path: string, body: unknown): Promise<{ ok: true; data: T } | { ok: false; error: string }> {
  const response = await fetch(path, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    return { ok: false, error: (data as { error?: string }).error || "Request failed" };
  }

  return { ok: true, data: data as T };
}

async function getJson<T>(path: string): Promise<T | null> {
  const response = await fetch(path);
  if (!response.ok) {
    return null;
  }

  return (await response.json()) as T;
}

export async function login(email: string, password: string) {
  return postJson<{ id: number; email: string }>("/api/login", { email, password });
}

export async function signup(email: string, password: string, username: string) {
  return postJson<{ id: number; email: string }>("/api/signup", { email, password, username });
}

export async function logout() {
  await fetch("/api/logout", { method: "POST" });
}

export async function getCurrentUser() {
  return getJson<{ id: number; email: string }>("/api/me");
}

export async function getHome() {
  return getJson<{ boards: Board[]; finds: DiscoveryProduct[] }>("/api/home");
}

export async function getBoards() {
  return getJson<{ boards: Board[] }>("/api/boards");
}

export async function getPublicBoards() {
  return getJson<{ boards: Board[] }>("/api/boards/public");
}

export async function getBoard(boardId: number) {
  return getJson<BoardDetails>(`/api/boards/${boardId}`);
}

export async function copyBoard(boardId: number) {
  return postJson<{ board: Board; copiedFindCount: number; skippedFindCount: number }>(`/api/boards/${boardId}/copy`, {});
}

export interface ChatResult {
  message: string;
  board: Board;
}

export async function sendChat(params: { message: string; boardId: number | null }) {
  return postJson<ChatResult>("/api/chat", params);
}

export async function discoverProduct(params: { url: string }) {
  const response = await fetch("/api/finds/discover", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(params),
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const errorData = data as { error?: string; duplicate?: boolean; find?: TriageProduct };
    return {
      ok: false as const,
      error: errorData.error || "Request failed",
      duplicate: errorData.duplicate ?? false,
      find: errorData.find,
    };
  }

  return {
    ok: true as const,
    data: data as { find: TriageProduct; duplicate?: boolean },
  };
}

export async function scrapeProduct(params: { url: string; boardId?: number }) {
  return postJson<{ find: TriageProduct; board?: Board; duplicate?: boolean }>("/api/finds/scrape", params);
}

export async function getFind(findId: number) {
  return getJson<{ find: TriageProduct; isOwner: boolean }>(`/api/finds/${findId}`);
}

export async function saveFindToBoard(params: {
  sourceFindId: number;
  boardId?: number;
  newBoard?: { label: string };
}) {
  return postJson<{ find: TriageProduct; board: Board }>("/api/finds/save", params);
}

export async function getBoardSuggestions(findId: number, options: { includeIdeas?: boolean } = {}) {
  const query = options.includeIdeas ? "?ideas=1" : "";
  return getJson<{
    suggestedBoards: BoardSuggestion[];
    existingBoards: Board[];
  }>(`/api/finds/${findId}/board-suggestions${query}`);
}

export async function assignFindToBoard(params: {
  findId: number;
  boardId?: number;
  newBoard?: { label: string };
}) {
  const { findId, ...body } = params;
  return postJson<{ find: TriageProduct; board: Board }>(`/api/finds/${findId}/assign`, body);
}

export async function setFindReaction(params: {
  boardId: number;
  findId: number;
  reaction: Reaction;
  passReason?: string;
}) {
  const { boardId, findId, ...body } = params;
  return fetch(`/api/boards/${boardId}/finds/${findId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export async function removeFindFromBoard(params: {
  boardId: number;
  findId: number;
}) {
  const { boardId, findId } = params;
  const response = await fetch(`/api/boards/${boardId}/finds/${findId}`, {
    method: "DELETE",
  });
  return response.ok;
}

export async function deleteFind(findId: number) {
  const response = await fetch(`/api/finds/${findId}`, { method: "DELETE" });
  return response.ok;
}

export async function updateFindTitle(findId: number, title: string) {
  return patchJson<{ find: TriageProduct }>(`/api/finds/${findId}`, { title });
}

export async function updateFindImagePosition(findId: number, position: ImagePosition) {
  return patchJson<{ find: TriageProduct }>(`/api/finds/${findId}`, { ...position });
}

export async function uploadImage(file: File): Promise<{ ok: true; data: { id: number; url: string } } | { ok: false; error: string }> {
  const formData = new FormData();
  formData.append("file", file);
  const response = await fetch("/api/images", { method: "POST", body: formData });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    return { ok: false, error: (data as { error?: string }).error || "Upload failed" };
  }
  return { ok: true, data: data as { id: number; url: string } };
}

export async function updateFindImageUrl(findId: number, imageUrl: string) {
  return patchJson<{ find: TriageProduct }>(`/api/finds/${findId}`, { imageUrl });
}

export async function createBoard(label: string) {
  return postJson<{ board: Board }>("/api/boards", { label });
}

export async function updateBoard(boardId: number, label: string) {
  return patchJson<{ board: Board }>(`/api/boards/${boardId}`, { label });
}

export async function updateBoardVisibility(boardId: number, visibility: "public" | "private") {
  return patchJson<{ board: Board }>(`/api/boards/${boardId}/visibility`, { visibility });
}

export async function deleteBoard(boardId: number) {
  const response = await fetch(`/api/boards/${boardId}`, { method: "DELETE" });
  return response.ok;
}

export async function getProfile() {
  return getJson<UserProfile>("/api/profile");
}

export async function getFinds(page: number) {
  return getJson<{ finds: FindsCard[]; total: number }>(`/api/finds?page=${page}`);
}

export async function getPublicFinds() {
  return getJson<{ finds: FindsCard[] }>("/api/finds/public");
}

export async function classifyInput(text: string) {
  return postJson<{ type: "search" | "conversation" }>("/api/classify-input", { text });
}

export async function searchFinds(query: string) {
  return getJson<{ finds: DiscoveryProduct[] }>(`/api/search?q=${encodeURIComponent(query)}`);
}

export function extractUrls(text: string): string[] {
  return Array.from(text.matchAll(/https?:\/\/[^\s]+/g), (match) => match[0]);
}
